import React from 'react';
import classnames from 'classnames';

import { trackGA4AndNavigate, trackGA4PreNav } from '@gumtree/shared/src/util/ga4-shared';
import { getDomainFromUrl } from '@gumtree/shared/src/util/url-service';
import { Icon, SubMenu, SubMenuItem } from '@gumtree/ui-library';
import MhcAlertsIcon from '@gumtree/ui-library/src/icon/svg/nav-bar/mhc-alerts.svg';
import MhcDetailsIcon from '@gumtree/ui-library/src/icon/svg/nav-bar/mhc-details.svg';
import MhcFavouritesIcon from '@gumtree/ui-library/src/icon/svg/nav-bar/mhc-favourites.svg';
import MhcHelpIcon from '@gumtree/ui-library/src/icon/svg/nav-bar/mhc-help.svg';
import MhcJobAdsIcon from '@gumtree/ui-library/src/icon/svg/nav-bar/mhc-job-ads.svg';
import MhcManageAdsIcon from '@gumtree/ui-library/src/icon/svg/nav-bar/mhc-manage-ads.svg';
import MhcMessagesIcon from '@gumtree/ui-library/src/icon/svg/nav-bar/mhc-messages.svg';
import MhcOrdersIcon from '@gumtree/ui-library/src/icon/svg/nav-bar/mhc-orders.svg';
import slug from '@gumtree/ui-library/src/utils/slug';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import { useShowPayShipFeature } from '@gumtree/shared/src/hooks/useShowPayShipFeature';
import { useJobsTrafficSplitResult } from '@gumtree/shared/src/hooks/useJobsTrafficSplitUrl';
import { trackManageJobAdsClickAndNavigate } from '@gumtree/shared/src/util/jobs-traffic-split-tracking';
import LinkButton from './link-button';
import getMenuItems from './user-sub-menu-data';
import LogoutForm from '../logout-form';

import './user-sub-menu.scss';

const mhcDesktopMenuIcons: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
    'Manage my Ads': MhcManageAdsIcon,
    'My Orders': MhcOrdersIcon,
    Messages: MhcMessagesIcon,
    Favourites: MhcFavouritesIcon,
    'My Alerts': MhcAlertsIcon,
    'My Details': MhcDetailsIcon,
    'Manage my Job Ads': MhcJobAdsIcon,
    'Help & Contact': MhcHelpIcon,
};

export default function UserSubMenu({
    isMhcDesktopExperiment = false,
    isOpen = false,
    pageType,
}: {
    isMhcDesktopExperiment?: boolean;
    isOpen?: boolean;
    pageType: string;
}) {
    const showPayShipFeature = useShowPayShipFeature();
    const { url: manageJobAdsUrl, linkDestination } = useJobsTrafficSplitResult(
        'https://recruiters.gumtree.com/job-listing/',
        'your-jobs'
    );
    const menuItems = React.useMemo(
        () => getMenuItems(showPayShipFeature, manageJobAdsUrl),
        [showPayShipFeature, manageJobAdsUrl]
    );

    return (
        <div
            data-testid="user-sub-menu"
            className={classnames({
                'mhc-desktop-user-sub-menu': isMhcDesktopExperiment,
                'user-sub-menu': true,
                open: isOpen,
            })}
            {...qaAttribute('user-sub-menu')}
            suppressHydrationWarning
        >
            <SubMenu>
                {menuItems.map(({ icon, label, url }) => {
                    const MhcDesktopMenuIcon = mhcDesktopMenuIcons[label];

                    return (
                        <SubMenuItem key={label}>
                            <LinkButton
                                isPadded
                                url={url}
                                {...qaAttribute(`user-menu-link-${slug(label)}`)}
                                onClick={(e) => {
                                    const payload: GA4.ClickPrimaryNavigation = {
                                        event: 'click_primary_navigation',
                                        linkDomain: getDomainFromUrl(url),
                                        linkText: label,
                                        linkURL: url,
                                        navigationCategory: 'menu',
                                        navigationHeader: undefined,
                                        navigationHierarchy: 'sub header',
                                        pageClickedFrom: pageType,
                                    };

                                    if (label === 'Manage my Job Ads') {
                                        e.preventDefault();
                                        trackGA4PreNav(payload);
                                        trackManageJobAdsClickAndNavigate({
                                            ctaLocation: 'side_menu',
                                            linkDestination,
                                            href: url,
                                        });
                                        return;
                                    }

                                    trackGA4AndNavigate<GA4.ClickPrimaryNavigation>({
                                        href: url,
                                        mouseEvent: e,
                                        payload,
                                    });
                                }}
                            >
                                {isMhcDesktopExperiment &&
                                    (MhcDesktopMenuIcon ? (
                                        <MhcDesktopMenuIcon aria-hidden="true" focusable="false" />
                                    ) : (
                                        <Icon type={icon} />
                                    ))}
                                {label}
                                {!isMhcDesktopExperiment && <Icon type="chevron-r" />}
                            </LinkButton>
                        </SubMenuItem>
                    );
                })}
                <SubMenuItem>
                    <LogoutForm isMhcDesktopMenu={isMhcDesktopExperiment} />
                </SubMenuItem>
            </SubMenu>
        </div>
    );
}
